import type { Product } from '@/api/goods'
import type { Branch } from '@/api/types'

/// Shelves of goods, pure. SPEC: specs/shop.md §2.4, §8; specs/roles.md R8.
///
/// Three answers that are never folded into one value — that fold was the
/// bug: `null` meant «every shelf summed» on the stock list and «the only
/// site» on a paper, so a write-off counted what was on the network and took
/// it from a shelf that had none.
/// - `all` — every shelf the caller sees, summed. A read only.
/// - `none` — the shelf with no club: a company before its first club, and
///   whatever was received there before one opened (it stays there).
/// - `club` — one club's shelf.
/// A paper is on exactly one shelf, never on `all`.

export type Shelf = Readonly<{ kind: 'all' }> | Readonly<{ kind: 'none' }> | Readonly<{ kind: 'club'; branchId: string }>
export type PaperShelf = Exclude<Shelf, Readonly<{ kind: 'all' }>>

export const EVERY_SHELF: Shelf = { kind: 'all' }
export const NO_CLUB: PaperShelf = { kind: 'none' }
export const clubShelf = (branchId: string): PaperShelf => ({ kind: 'club', branchId })

/// One string per shelf — for a URL and a select: `all`, `none`, a club's id.
export const shelfKey = (shelf: Shelf): string => (shelf.kind === 'club' ? shelf.branchId : shelf.kind)

export const shelfFromKey = (key: string | null | undefined): Shelf =>
  !key || key === 'all' ? EVERY_SHELF : key === 'none' ? NO_CLUB : clubShelf(key)

export const sameShelf = (a: Shelf, b: Shelf): boolean => shelfKey(a) === shelfKey(b)

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/// A page's `club` search parameter, as `shelfKey` wrote it — absent for
/// every shelf, and absent for anything that is neither `none` nor an id (a
/// hand-edited URL lists every shelf rather than failing).
export const clubSearch = (raw: unknown): { club?: string } =>
  typeof raw === 'string' && (raw === 'none' || UUID.test(raw)) ? { club: raw } : {}

/// The `branchId` of a read (services/shop): absent for every shelf, `none`
/// for the shelf with no club, the club's id.
export const shelfQuery = (shelf: Shelf): string | undefined => (shelf.kind === 'all' ? undefined : shelfKey(shelf))

/// The `branchId` a paper is written with.
export const paperBranchId = (shelf: PaperShelf): string | null => (shelf.kind === 'club' ? shelf.branchId : null)

/// The shelf a written paper is on.
export const shelfOfPaper = (branchId: string | null): PaperShelf => (branchId === null ? NO_CLUB : clubShelf(branchId))

/// Whether a shelf's list says anything is on it.
export const holdsGoods = (products: readonly Pick<Product, 'onHand'>[]): boolean => products.some((p) => p.onHand > 0)

export type ShelfChoiceInput = Readonly<{
  /// Open clubs the caller may put a paper in (`placeableBranches`).
  clubs: readonly Pick<Branch, 'id'>[]
  /// Whole-company reach: a paper with no club is the caller's to write (R8).
  noClub: boolean
  /// The paper takes goods off a shelf (a write-off, a sale) rather than
  /// putting them on one (a receipt).
  takes: boolean
  /// The shelf with no club still holds goods.
  noClubHolds: boolean
  /// The shelf the person is looking at — the page's filter.
  preferred: Shelf
}>

export type ShelfChoice = Readonly<{
  /// Where this paper may go, clubs first.
  options: readonly PaperShelf[]
  /// What is chosen before the person chooses: the page's shelf when it is
  /// one of the options, else the only club, else nothing — with two clubs
  /// a paper is never put in one of them by default.
  initial: PaperShelf | null
}>

export const paperShelfChoice = (input: ShelfChoiceInput): ShelfChoice => {
  const clubs = input.clubs.map((club) => clubShelf(club.id))
  // The shelf with no club is the ONLY shelf of a company with no clubs.
  // Once clubs exist it is a way out for what is still on it — never a new
  // place to put goods (specs/shop.md §8).
  const withNoClub = input.noClub && (clubs.length === 0 || (input.takes && input.noClubHolds))
  const options = [...clubs, ...(withNoClub ? [NO_CLUB] : [])]
  const preferred = options.find((option) => sameShelf(option, input.preferred))
  const only = options.length === 1 ? options[0] : clubs.length === 1 ? clubs[0] : undefined
  return { options, initial: preferred ?? only ?? null }
}

/// The shelves a list can be narrowed to — every shelf the caller reads:
/// the clubs it reaches (archived ones too, their papers are still papers)
/// and «без клубу» while that shelf holds goods. One shelf is no choice.
export const shelfFilterOptions = (
  clubs: readonly Pick<Branch, 'id'>[],
  noClubHolds: boolean,
): readonly Shelf[] => {
  const shelves = [...clubs.map((club) => clubShelf(club.id)), ...(clubs.length > 0 && noClubHolds ? [NO_CLUB] : [])]
  return shelves.length >= 2 ? [EVERY_SHELF, ...shelves] : []
}
